/**
 * Leads bulk-action dialogs — Add to segment + Bulk WhatsApp send + Bulk Email.
 * Extracted from Leads.jsx for readability. Feb 2026 · R14 · R133.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, MessageCircle, Sparkles, Plus, Send, Mail, Rocket, AlertTriangle, Wand2, RefreshCw, CheckCircle2 } from "lucide-react";
import SmartRichText from "@/components/SmartRichText";

/**
 * R267m · Feb 2026 — Compact relative-time formatter used inside
 * the auto-fill chip: "2 h ago" / "5 min ago" / "3 d ago".
 */
function _relTime(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs} h ago`;
    return `${Math.round(hrs / 24)} d ago`;
  } catch { return ""; }
}

// ──────────────────── Add-to-segment dialog ────────────────────

/**
 * R267c · Feb 2026 — One-click "Prepend +91" fixer used inside the
 * bulk-WA preflight card. Prompts once for the country code (default
 * +91, remembered across sessions per browser via localStorage), then
 * PATCHes all lead phones and re-runs the preflight.
 */
function PrependCountryCodeButton({ wsId, rows, onDone }) {
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [cc, setCc] = useState(() => {
    try { return localStorage.getItem("bulk-wa-default-cc") || "+91"; }
    catch { return "+91"; }
  });
  // R267d · Feb 2026 — pre-fill from the workspace-level default
  // so single-region tenants (Aquarius Global) don't have to
  // re-type +91 every time.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${wsId}/settings/communications`);
        if (!cancelled && data?.default_country_code) {
          setCc(data.default_country_code);
        }
      } catch { /* silent — falls back to localStorage/+91 */ }
    })();
    return () => { cancelled = true; };
  }, [wsId]);
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/leads/bulk-prepend-country-code`,
        { lead_ids: rows.map((r) => r.lead_id), country_code: cc });
      try { localStorage.setItem("bulk-wa-default-cc", cc); } catch { /* silent */ }
      toast.success(`Prefixed ${data.updated_count} lead(s) with ${data.country_code}` +
        (data.skipped_count ? ` · ${data.skipped_count} skipped` : ""));
      setPickerOpen(false);
      onDone?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Country-code fix failed")); }
    finally { setBusy(false); }
  };
  if (pickerOpen) {
    return (
      <div className="flex items-center gap-1"
           data-testid="bulk-wa-cc-picker">
        <input
          type="text" value={cc}
          onChange={(e) => setCc(e.target.value)}
          className="h-6 px-1.5 py-0.5 text-[11px] font-mono border border-amber-300 bg-white w-16"
          placeholder="+91"
          data-testid="bulk-wa-cc-input"
        />
        <button type="button" disabled={busy} onClick={apply}
                className="text-[10px] px-1.5 py-0.5 bg-amber-800 text-white hover:bg-amber-900 disabled:opacity-50"
                data-testid="bulk-wa-cc-apply">
          {busy ? "fixing…" : `Fix ${rows.length}`}
        </button>
        <button type="button" onClick={() => setPickerOpen(false)}
                className="text-[10px] text-amber-800 underline">
          cancel
        </button>
      </div>
    );
  }
  return (
    <button type="button"
            onClick={() => setPickerOpen(true)}
            className="text-[10px] px-1.5 py-0.5 border border-amber-400 text-amber-900 hover:bg-amber-100 flex items-center gap-1"
            data-testid="bulk-wa-cc-fix-btn"
            title={`Prepend a country code to all ${rows.length} lead(s) in one click.`}>
      <Wand2 className="w-3 h-3" /> Fix with country code
    </button>
  );
}


// ──────────────────── Add-to-segment dialog ────────────────────

export function AddToSegmentDialog({ leadIds, wsId, segments, currentFilters, onClose, onDone }) {
  const [mode, setMode] = useState("existing"); // existing | new
  const [existingId, setExistingId] = useState("");
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [applyFilters, setApplyFilters] = useState(false);
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    if (leadIds.length === 0) return toast.error("No leads selected");
    setBusy(true);
    try {
      let segId = existingId;
      if (mode === "new") {
        if (!newName.trim()) { setBusy(false); return toast.error("Segment name required"); }
        const payload = {
          name: newName.trim(),
          description: newDesc.trim() || undefined,
          filters: applyFilters ? (currentFilters || {}) : {},
        };
        const { data } = await api.post(`/workspaces/${wsId}/lead-segments`, payload);
        segId = data.id;
      } else {
        if (!segId) { setBusy(false); return toast.error("Pick a segment"); }
      }
      const { data: att } = await api.post(`/workspaces/${wsId}/lead-segments/${segId}/attach-leads`, { lead_ids: leadIds });
      toast.success(`Attached ${att.attached} lead${att.attached === 1 ? "" : "s"} to segment`);
      if (att.invalid_lead_ids?.length) {
        toast.warning(`${att.invalid_lead_ids.length} lead(s) were invalid and skipped`);
      }
      onDone?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Add to segment failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !busy && !v && onClose()}>
      <DialogContent className="max-w-md rounded-none">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" /> Add {leadIds.length} lead{leadIds.length === 1 ? "" : "s"} to segment
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-xs">
            <button onClick={() => setMode("existing")} className={`px-2 py-1 border ${mode === "existing" ? "border-primary text-primary" : "border-neutral-300 text-neutral-600"}`} data-testid="lead-segment-mode-existing">
              Add to existing
            </button>
            <button onClick={() => setMode("new")} className={`px-2 py-1 border ${mode === "new" ? "border-primary text-primary" : "border-neutral-300 text-neutral-600"}`} data-testid="lead-segment-mode-new">
              Create new
            </button>
          </div>
          {mode === "existing" ? (
            <div>
              <Label className="text-xs">Segment</Label>
              <Select value={existingId} onValueChange={setExistingId}>
                <SelectTrigger className="h-9 rounded-none" data-testid="lead-segment-pick"><SelectValue placeholder="Choose a saved segment…" /></SelectTrigger>
                <SelectContent>
                  {segments?.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(!segments || segments.length === 0) && (
                <div className="text-[11px] text-muted-foreground mt-1 italic">No saved segments yet — switch to &quot;Create new&quot;.</div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <div>
                <Label className="text-xs">Name *</Label>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} className="h-9 rounded-none" placeholder="e.g. Q1 Hot Prospects" data-testid="lead-segment-new-name" />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Input value={newDesc} onChange={(e) => setNewDesc(e.target.value)} className="h-9 rounded-none" />
              </div>
              <label className="flex items-center gap-2 text-xs pt-1">
                <input type="checkbox" checked={applyFilters} onChange={(e) => setApplyFilters(e.target.checked)} data-testid="lead-segment-apply-filters" />
                Also apply the current filters (auto-attach matching leads going forward)
              </label>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={apply} disabled={busy} data-testid="lead-segment-apply">
            {busy ? "Attaching…" : (mode === "new" ? <><Plus className="w-4 h-4 mr-1" /> Create &amp; attach</> : "Attach")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// R135c · Client-side merge tag renderer — mirrors the server's
// _personalise / _personalise_var so recipient previews match the
// actual send.
function renderMergeTags(text, lead) {
  if (!text) return "";
  const name = (lead?.name || "").trim();
  const first = name.split(" ", 1)[0] || "";
  return String(text)
    .replaceAll("{{name}}", name || "there")
    .replaceAll("{{first_name}}", first || "there")
    .replaceAll("{{company}}", (lead?.company || "").trim())
    .replaceAll("{{email}}", (lead?.email || "").trim())
    .replaceAll("{{title}}", (lead?.title || "").trim())
    .replaceAll("{{industry}}", (lead?.industry || "").trim())
    .replaceAll("{{owner_name}}", (lead?.owner_name || "").trim());
}

// R135e · Client-side rate-limit heuristic. Providers roughly:
//   Gmail SMTP ≈ 60/min, Outlook SMTP ≈ 30/min, Google Workspace ≈ 100/min,
//   generic SES ≈ 14/sec. We warn if our effective rate exceeds a safe 30/min
//   floor. `interval_seconds > 0` puts us in staggered mode.
function computeRateWarning(intervalSec, targetCount) {
  if (targetCount <= 30) return null;   // below any provider throttle
  const perMin = intervalSec > 0 ? (60 / intervalSec) : Infinity;
  const totalMin = intervalSec > 0 ? (targetCount * intervalSec) / 60 : 0;
  if (intervalSec === 0) {
    return {
      level: "danger",
      msg: `Bursting ${targetCount} messages with 0-second stagger will very likely trip your provider's rate cap. Add at least a 2-second stagger.`,
    };
  }
  if (perMin > 100) {
    return {
      level: "danger",
      msg: `${perMin.toFixed(0)}/min exceeds most SMTP + Interakt limits (~60-100/min). Increase stagger to at least 1s.`,
    };
  }
  if (perMin > 60) {
    return {
      level: "warn",
      msg: `${perMin.toFixed(0)}/min may hit Gmail / Outlook per-minute caps. Total: ~${totalMin.toFixed(1)} min.`,
    };
  }
  return {
    level: "ok",
    msg: `~${perMin.toFixed(0)}/min · will finish in ~${totalMin.toFixed(1)} min.`,
  };
}

// R135e · Deliverability heuristic — cheap client-side spam-risk score
// (0-100, higher = riskier). Returns { score, flags[] }.
function computeDeliverabilityScore({ subject, bodyHtml, signature, attachmentsCount }) {
  const flags = [];
  let risk = 0;
  const sub = (subject || "").trim();
  const body = (bodyHtml || "");
  const bodyText = body.replace(/<[^>]+>/g, "").trim();

  if (!sub) { flags.push({ level: "danger", msg: "Empty subject" }); risk += 30; }
  else {
    if (sub.length > 80) { flags.push({ level: "warn", msg: "Subject > 80 chars — most clients truncate" }); risk += 10; }
    const capsChars = sub.replace(/[^A-Z]/g, "").length;
    if (capsChars > 0 && capsChars / sub.length > 0.5) {
      flags.push({ level: "danger", msg: "Subject is mostly UPPERCASE — high spam signal" }); risk += 20;
    }
    if (/!{2,}/.test(sub) || (sub.match(/!/g) || []).length > 2) {
      flags.push({ level: "warn", msg: "Multiple ! in subject" }); risk += 10;
    }
    if (/\bfree\b|\b\$\$|\bwin\b|\bguarantee\b|\bcash\b|urgent/i.test(sub)) {
      flags.push({ level: "warn", msg: "Common spam trigger words in subject" }); risk += 15;
    }
  }
  if (!bodyText || bodyText.length < 30) {
    flags.push({ level: "warn", msg: "Body is very short — providers penalise thin content" });
    risk += 10;
  }
  const linkCount = (body.match(/<a\s+[^>]*href=/gi) || []).length;
  if (linkCount > 5) {
    flags.push({ level: "warn", msg: `${linkCount} links in body — 3 or fewer is safer` });
    risk += 10;
  }
  if (linkCount === 0 && bodyText.length > 200) {
    flags.push({ level: "info", msg: "No links — consider adding one clear CTA" });
  }
  if (!signature || !signature.trim()) {
    flags.push({ level: "warn", msg: "No signature — recipients trust emails with a signed-off human name" });
    risk += 10;
  }
  if (attachmentsCount > 3) {
    flags.push({ level: "warn", msg: `${attachmentsCount} attachments — 1-2 is more inbox-friendly` });
    risk += 5;
  }
  // Only-image / no-text body is a well-known spam signal.
  if (bodyText.length < 20 && /<img/i.test(body)) {
    flags.push({ level: "danger", msg: "Body is mostly images with no readable text" });
    risk += 20;
  }
  risk = Math.max(0, Math.min(100, risk));
  const score = 100 - risk;
  return { score, flags };
}

// R135f · Subject line advisory — most email clients truncate the
// preview around 40 chars on iOS and 60 chars on Gmail cards.
function computeSubjectHint(sub) {
  const s = (sub || "");
  const len = s.length;
  const mobilePreview = s.slice(0, 40);
  let tone = "text-muted-foreground";
  let msg = "";
  if (!s.trim()) { tone = "text-rose-600"; msg = "empty"; }
  else if (len > 80) { tone = "text-amber-700"; msg = "very long — Gmail truncates after ~60"; }
  else if (len > 60) { tone = "text-amber-700"; msg = "may truncate on desktop cards"; }
  else if (len > 40) { tone = "text-emerald-700"; msg = "great desktop length · iOS truncates after 40"; }
  else { tone = "text-emerald-700"; msg = "reads in full on mobile + desktop"; }
  return { len, tone, msg, mobilePreview };
}

// R135f · Sender warmup badge — mailboxes < 30 days old are still
// warming reputation and shouldn't take huge blasts.
function computeWarmupBadge(mailbox) {
  const iso = mailbox?.created_at || mailbox?.createdAt;
  if (!iso) return null;
  try {
    const created = new Date(iso).getTime();
    if (!created) return null;
    const days = Math.floor((Date.now() - created) / (1000 * 60 * 60 * 24));
    if (days < 0) return null;
    if (days < 7) return { level: "danger", label: `Day ${days} · cold` };
    if (days < 30) return { level: "warn", label: `Day ${days} · warming` };
    if (days < 90) return { level: "ok", label: `Day ${days} · warm` };
    return null;   // established, no badge needed
  } catch { return null; }
}

// ──────────────────── Bulk WhatsApp dialog ────────────────────

export function BulkWhatsAppDialog({ leadIds, wsId, segmentId, onClose, onDone }) {
  const [mode, setMode] = useState("template");
  // R268eeee · Feb 2026 — Which lead field to dial on. Persisted per
  // browser so PMs don't have to reset it every send.
  const [numberSource, setNumberSource] = useState(() => {
    try { return localStorage.getItem("bulk-wa-number-source") || "phone_first"; }
    catch { return "phone_first"; }
  });
  const [templateName, setTemplateName] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  // R135c · Per-slot variables (each may contain merge tags for per-lead render).
  const [varSlots, setVarSlots] = useState([]);  // ["{{first_name}}", "{{company}}", ...]
  const [text, setText] = useState("");
  const [force, setForce] = useState(false);
  // R133 · Feb 2026 — Stagger control (seconds between sends).
  const [intervalSec, setIntervalSec] = useState(0);
  // R135 · Send Time Optimizer.
  const [sendWindow, setSendWindow] = useState("");     // "09:00-18:00"
  const [defaultTz, setDefaultTz] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [campaign, setCampaign] = useState(null);
  // R135c · Recipient preview.
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  // R135g · Template header media upload + Scheduled sends.
  const [headerMediaUrl, setHeaderMediaUrl] = useState("");
  const [headerFilename, setHeaderFilename] = useState("");
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [waScheduledAt, setWaScheduledAt] = useState("");
  // R267m · Feb 2026 — Auto-fill state for the header-media field.
  // `mediaSource` is "last_success" | "interakt_sample" | "user_upload"
  // | "none" — the FE renders a different chip label per source so
  // agents know whether we're reusing their file, Interakt's sample,
  // or asking for a fresh upload.
  const [mediaSource, setMediaSource] = useState("none");
  const [mediaMeta, setMediaMeta] = useState(null);
  // R267b · Feb 2026 — Preflight validation card. Guardrails leads
  // whose phone is missing / invalid / lacks a country code so ops
  // can spot dropouts BEFORE firing the campaign (Aquarius Global
  // was silently defaulting to +91 for unprefixed numbers).
  const [preflight, setPreflight] = useState(null);
  const [preflightBusy, setPreflightBusy] = useState(false);
  const [preflightOverride, setPreflightOverride] = useState(false);

  const targetCount = leadIds?.length || 0;

  // Fetch approved WA templates (Interakt + saved library) on open.
  useEffect(() => {
    if (!wsId) return;
    setTemplatesLoading(true);
    Promise.allSettled([
      api.get(`/workspaces/${wsId}/marketing/wa-templates/interakt`),
      api.get(`/workspaces/${wsId}/wa/saved-templates`),
    ]).then(([interaktRes, libRes]) => {
      const interakt = interaktRes.status === "fulfilled"
        ? (interaktRes.value.data?.rows || []) : [];
      const lib = libRes.status === "fulfilled"
        ? (libRes.value.data?.rows || libRes.value.data || []) : [];
      const seen = new Set();
      const merged = [];
      for (const t of [...interakt, ...lib]) {
        const key = t.name || t.template_name;
        if (!key || seen.has(key)) continue;
        seen.add(key);
        merged.push({ ...t, __source: interakt.includes(t) ? "approved" : "library" });
      }
      setTemplates(merged);
    }).finally(() => setTemplatesLoading(false));
  }, [wsId]);

  // Selected template + its {{n}} placeholder count.
  const matchedTpl = useMemo(() => templates.find(
    (t) => (t.name || t.template_name) === templateName), [templates, templateName]);
  const placeholderCount = useMemo(() => {
    const bodyText = matchedTpl?.body_text || matchedTpl?.template_body || matchedTpl?.body || "";
    if (!bodyText) return 0;
    const matches = String(bodyText).match(/\{\{\s*(\d+)\s*\}\}/g) || [];
    const nums = matches.map((m) => parseInt(m.replace(/[^\d]/g, ""), 10)).filter((n) => !isNaN(n));
    return nums.length ? Math.max(...nums) : 0;
  }, [matchedTpl]);
  // R135g · Header media requirement — templates like edutrac_oman_jeet
  // carry `header_type: "IMAGE"/"DOCUMENT"/"VIDEO"`. When set the FE
  // must upload a file before send.
  const templateHeaderType = useMemo(
    () => ((matchedTpl?.header_type || "").toUpperCase()),
    [matchedTpl]);
  const needsHeaderMedia = ["IMAGE", "DOCUMENT", "VIDEO"].includes(templateHeaderType);

  // R267m · Feb 2026 — Option C auto-fill for header media. When the
  // agent picks a media-header template, hit the backend for a
  // pre-fillable URL:
  //   1. Prior successful send (`last_success`)
  //   2. Interakt-approved sample (`interakt_sample`)
  //   3. Neither — user must upload.
  // If the agent has already uploaded a fresh file this session,
  // don't overwrite it.
  useEffect(() => {
    if (!wsId || !templateName || !needsHeaderMedia) {
      setMediaSource("none"); setMediaMeta(null);
      return;
    }
    if (mediaSource === "user_upload") return; // preserve fresh upload
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/workspaces/${wsId}/wa/saved-templates/by-name/${encodeURIComponent(templateName)}/last-media`);
        if (cancelled) return;
        if (data?.has_last && data?.url) {
          setHeaderMediaUrl(data.url);
          setHeaderFilename(data.filename || "");
          setMediaSource(data.source || "last_success");
          setMediaMeta({ at: data.at, by_name: data.by_name });
        } else {
          // No auto-fill available — clear only if the user hasn't
          // already uploaded something this session.
          setMediaSource("none"); setMediaMeta(null);
        }
      } catch { /* silent — falls back to upload UI */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsId, templateName, needsHeaderMedia]);

  // R135c · When a template is picked, auto-map slots to sensible
  // per-lead merge tags. The heuristic: slot 1 → first_name, slot 2 →
  // company, slot 3 → title, slot 4+ → literal empty.
  const DEFAULT_SLOT_MAP = ["{{first_name}}", "{{company}}", "{{title}}", "{{industry}}", "{{owner_name}}"];
  useEffect(() => {
    if (placeholderCount === 0) { setVarSlots([]); return; }
    setVarSlots(Array.from({ length: placeholderCount }, (_, i) => DEFAULT_SLOT_MAP[i] || ""));
  }, [templateName, placeholderCount]);

  const MERGE_OPTIONS = [
    { label: "First name", value: "{{first_name}}" },
    { label: "Full name", value: "{{name}}" },
    { label: "Company", value: "{{company}}" },
    { label: "Title", value: "{{title}}" },
    { label: "Industry", value: "{{industry}}" },
    { label: "Email", value: "{{email}}" },
    { label: "Owner name", value: "{{owner_name}}" },
    { label: "Literal text", value: "__literal__" },
  ];

  // R135c · Recipient preview — pull first 3 leads and render fully
  // personalised copies exactly as they'll go out.
  const loadPreview = async () => {
    try {
      let previewLeads = [];
      if (leadIds?.length) {
        const first3 = leadIds.slice(0, 3);
        const rows = await Promise.all(first3.map(async (lid) => {
          try {
            const r = await api.get(`/workspaces/${wsId}/leads`);
            const found = (r.data || []).find((x) => x.id === lid);
            return found;
          } catch { return null; }
        }));
        previewLeads = rows.filter(Boolean);
      } else if (segmentId) {
        // Segment mode — pull the segment leads via list-leads then slice.
        const r = await api.get(`/workspaces/${wsId}/leads`);
        previewLeads = (r.data || []).slice(0, 3);
      }
      setPreview(previewLeads);
      setShowPreview(true);
    } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); }
  };

  // R267b · Feb 2026 — Preflight the target set against the phone
  // validator. Called on mount + on Preview click + on Send. Silent
  // mode is used inside `send()` so the toast doesn't double-fire.
  const runPreflight = async ({ silent = false } = {}) => {
    if (targetCount === 0 && !segmentId) return null;
    setPreflightBusy(true);
    try {
      const payload = leadIds?.length
        ? { lead_ids: leadIds, number_source: numberSource }
        : { segment_id: segmentId, number_source: numberSource };
      const { data } = await api.post(
        `/workspaces/${wsId}/leads/bulk-wa-preflight`, payload);
      setPreflight(data);
      const problems = (data.counts?.missing || 0)
        + (data.counts?.invalid || 0)
        + (data.counts?.no_country_code || 0);
      if (!silent) {
        if (problems === 0) {
          toast.success(`All ${data.total} recipient${data.total === 1 ? "" : "s"} look valid.`);
        } else {
          toast.warning(`${problems} of ${data.total} recipient(s) have phone issues — see the preflight card.`);
        }
      }
      return data;
    } catch (e) {
      if (!silent) toast.error(apiErrorMessage(e, "Preflight failed"));
      return null;
    } finally { setPreflightBusy(false); }
  };

  // Auto-run once when the dialog opens with a resolved target set.
  useEffect(() => {
    if (!wsId) return;
    if (targetCount === 0 && !segmentId) return;
    runPreflight({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsId, targetCount, segmentId]);

  // R135 · Poll campaign progress like the email dialog.
  useEffect(() => {
    if (!campaign?.campaign_id || campaign.state === "done") return undefined;
    const t = setInterval(async () => {
      try {
        const r = await api.get(
          `/workspaces/${wsId}/sales/campaigns/${campaign.campaign_id}`);
        setCampaign((prev) => ({ ...prev, ...r.data }));
        if (r.data.state === "done") {
          toast.success(`Campaign complete · ${r.data.sent} sent · ${r.data.failed} failed`);
          onDone?.();
        }
      } catch { /* keep polling */ }
    }, 2000);
    return () => clearInterval(t);
  }, [campaign?.campaign_id, campaign?.state, wsId, onDone]);

  // R135g · Upload a media file for template header (image / pdf / video).
  const uploadHeaderMedia = async (file) => {
    if (!file) return;
    setUploadingMedia(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/workspaces/${wsId}/wa/media/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      // Use the public URL that Interakt can reach.
      setHeaderMediaUrl(data.public_url || data.download_url || "");
      setHeaderFilename(data.original_filename || file.name);
      // R267m · Fresh upload takes precedence over auto-fill.
      setMediaSource("user_upload");
      setMediaMeta({ size_kb: Math.round((data.size_bytes || file.size) / 1024) });
      // R267l · Show the ffmpeg re-encode report if the server
      // transcoded the video — so agents know the file was made
      // Meta-compatible for them (guards against 131053).
      if (data.reencode_report?.transcoded) {
        const rr = data.reencode_report;
        toast.success(
          `Attached ${data.original_filename || file.name} · re-encoded to H.264 Baseline (${Math.round(rr.output_size / 1024)} KB) for Meta compatibility`);
      } else {
        toast.success(`Attached ${data.original_filename || file.name}`);
      }
      // Surface upload warnings from the backend (size-near-limit,
      // uncommon container, cloudconvert tip on video).
      (data.upload_warnings || []).forEach((w) => {
        if (w.code === "video_meta_compat_tip") return; // silent tip
        toast.warning(w.message);
      });
    } catch (e) { toast.error(apiErrorMessage(e, "Media upload failed")); }
    finally { setUploadingMedia(false); }
  };

  const send = async () => {
    if (targetCount === 0 && !segmentId) return toast.error("No leads or segment selected");
    if (mode === "template" && !templateName.trim()) return toast.error("Pick a template");
    if (mode === "session" && !text.trim()) return toast.error("Message text required");
    if (mode === "template" && needsHeaderMedia && !headerMediaUrl) {
      return toast.error(`Template needs a ${templateHeaderType.toLowerCase()} header — upload one first.`);
    }
    // R267b · Feb 2026 — Refresh preflight one last time before firing
    // so ops can't accidentally skip the guardrail (Aquarius Global
    // report). If issues remain and override wasn't ticked, block.
    let latest = preflight;
    if (!latest) {
      latest = await runPreflight({ silent: true });
    }
    const problems = latest
      ? (latest.counts?.missing || 0)
        + (latest.counts?.invalid || 0)
        + (latest.counts?.no_country_code || 0)
      : 0;
    if (problems > 0 && !preflightOverride) {
      toast.error(`${problems} lead(s) have missing or invalid phones — review the preflight card and tick "Send anyway" to continue.`);
      return;
    }
    if (!window.confirm(`Send WhatsApp to ${segmentId ? "the segment" : `${targetCount} lead(s)`}?${intervalSec > 0 ? ` (staggered · ${intervalSec}s each)` : ""}${sendWindow ? ` — held to recipient's ${sendWindow} local` : ""}${waScheduledAt ? ` — scheduled for ${new Date(waScheduledAt).toLocaleString()}` : ""}`)) return;
    setBusy(true);
    try {
      const payload = {
        ...(leadIds?.length ? { lead_ids: leadIds } : { segment_id: segmentId }),
        mode,
        force,
        interval_seconds: intervalSec,
        campaign_label: label.trim() || null,
        send_window_local: sendWindow.trim() || null,
        default_timezone: defaultTz.trim() || null,
        scheduled_at: waScheduledAt ? new Date(waScheduledAt).toISOString() : null,
        // R268eeee · Feb 2026 — Forward the user-picked source so the
        // backend dials the right column.
        number_source: numberSource,
        ...(mode === "template"
            ? { template_name: templateName.trim(),
                body_vars: varSlots,
                header_media_url: headerMediaUrl || null,
                header_filename: headerFilename || null,
                language_code: matchedTpl?.language_code || matchedTpl?.language || "en" }
            : { text: text.trim() }),
      };
      const { data } = await api.post(`/workspaces/${wsId}/leads/bulk-wa-send`, payload);
      if (!data.campaign_id) {
        toast.info("No matching recipients");
        onClose?.();
        return;
      }
      setCampaign({ campaign_id: data.campaign_id, total: data.total,
                     state: "queued", sent: 0, failed: 0, skipped: 0, processed: 0 });
      toast.success(`Campaign queued · ${data.total} recipients`);
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk send failed")); }
    finally { setBusy(false); }
  };

  const pct = campaign?.total > 0
    ? Math.min(100, Math.round((campaign.processed / campaign.total) * 100))
    : 0;

  // R135e · Rate-limit advisory (Interakt WA ≈ 80 msgs/sec on WABA;
  // we conservatively warn above 100/min).
  const waRateWarning = computeRateWarning(intervalSec, targetCount);

  return (
    <Dialog open onOpenChange={(v) => !busy && !v && onClose()}>
      <DialogContent className="max-w-lg w-[94vw] max-h-[92vh] p-0 gap-0 rounded-none overflow-hidden flex flex-col">
        <DialogHeader className="px-5 pt-5 pb-3 shrink-0 border-b border-border">
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-emerald-500" /> Bulk WhatsApp to {targetCount || "segment"} lead{targetCount === 1 ? "" : "s"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 flex-1 overflow-y-auto px-5 py-4">
          {!campaign && (
            <>
              <div className="flex items-center gap-2 text-xs">
                <button onClick={() => setMode("template")} className={`px-2 py-1 border ${mode === "template" ? "border-primary text-primary" : "border-neutral-300 text-neutral-600"}`} data-testid="bulk-wa-mode-template">
                  Template message
                </button>
                <button onClick={() => setMode("session")} className={`px-2 py-1 border ${mode === "session" ? "border-primary text-primary" : "border-neutral-300 text-neutral-600"}`} data-testid="bulk-wa-mode-session">
                  Free-text (24h window)
                </button>
              </div>
              {/* R268eeee · Feb 2026 — Which field to dial. Users
                  sometimes save the WA number in the wrong column so
                  a hard-coded pick would silently drop half the
                  audience. Persisted per browser via localStorage. */}
              <div>
                <Label className="text-xs">Which number to send to?</Label>
                <Select
                  value={numberSource}
                  onValueChange={(v) => {
                    setNumberSource(v);
                    try { localStorage.setItem("bulk-wa-number-source", v); } catch { /* silent */ }
                    setPreflight(null);   // stale — re-preflight will fire on Preview
                  }}
                >
                  <SelectTrigger className="rounded-none h-8 text-xs" data-testid="bulk-wa-number-source">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone_first" data-testid="bulk-wa-number-source-phone-first">
                      Phone (fallback to WhatsApp)
                    </SelectItem>
                    <SelectItem value="whatsapp_first" data-testid="bulk-wa-number-source-wa-first">
                      WhatsApp (fallback to Phone)
                    </SelectItem>
                    <SelectItem value="phone_only" data-testid="bulk-wa-number-source-phone-only">
                      Phone only
                    </SelectItem>
                    <SelectItem value="whatsapp_only" data-testid="bulk-wa-number-source-wa-only">
                      WhatsApp only
                    </SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-muted-foreground mt-1">
                  Rows with a blank number (per the rule above) will be skipped.
                </div>
              </div>
              {mode === "template" ? (
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">Interakt template *</Label>
                    <Select value={templateName} onValueChange={setTemplateName}>
                      <SelectTrigger className="rounded-none" data-testid="bulk-wa-template">
                        <SelectValue placeholder={templatesLoading ? "Loading templates…" : (templates.length ? "Pick an approved template…" : "No templates found — configure Interakt or add via WA Templates library")} />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((t) => {
                          const key = t.name || t.template_name;
                          const lang = t.language || t.language_code || t.template_language || "en";
                          const status = (t.status || t.template_status || "").toUpperCase();
                          return (
                            <SelectItem key={`${key}-${lang}`} value={key}>
                              <span className="flex items-center gap-2">
                                <span className="font-medium">{key}</span>
                                <span className="text-[10px] text-muted-foreground">· {lang}</span>
                                {status && status !== "APPROVED" && (
                                  <span className="text-[10px] px-1 border border-amber-300 text-amber-700">{status}</span>
                                )}
                                {t.__source === "library" && (
                                  <span className="text-[10px] px-1 border border-neutral-300 text-neutral-500">library</span>
                                )}
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {matchedTpl && (
                      <div className="mt-1 border border-emerald-200 bg-emerald-50/40 p-2 text-[11px] whitespace-pre-wrap"
                           data-testid="bulk-wa-template-preview">
                        {matchedTpl.body_text || matchedTpl.template_body || matchedTpl.body || <span className="text-muted-foreground italic">No body preview available</span>}
                      </div>
                    )}
                  </div>
                  {placeholderCount > 0 ? (
                    <div className="space-y-2">
                      <Label className="text-xs">Body variables — {placeholderCount} placeholder{placeholderCount === 1 ? "" : "s"} detected · auto-filled per-lead</Label>
                      {varSlots.map((val, idx) => {
                        const isLiteral = val && !val.startsWith("{{");
                        const currentOpt = isLiteral ? "__literal__" : val;
                        return (
                          <div key={idx} className="flex items-center gap-2"
                               data-testid={`bulk-wa-var-slot-${idx + 1}`}>
                            <span className="text-[10px] font-mono px-1.5 py-0.5 bg-neutral-100 border border-neutral-200">
                              {`{{${idx + 1}}}`}
                            </span>
                            <Select value={currentOpt}
                                    onValueChange={(v) => {
                                      const next = [...varSlots];
                                      next[idx] = v === "__literal__" ? "" : v;
                                      setVarSlots(next);
                                    }}>
                              <SelectTrigger className="rounded-none h-8 w-[160px]"
                                             data-testid={`bulk-wa-var-slot-${idx + 1}-picker`}>
                                <SelectValue placeholder="Pick field…" />
                              </SelectTrigger>
                              <SelectContent>
                                {MERGE_OPTIONS.map((o) => (
                                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {isLiteral || currentOpt === "__literal__" ? (
                              <Input value={val}
                                     onChange={(e) => {
                                       const next = [...varSlots]; next[idx] = e.target.value; setVarSlots(next);
                                     }}
                                     placeholder="Literal text for every lead"
                                     className="rounded-none h-8 flex-1"
                                     data-testid={`bulk-wa-var-slot-${idx + 1}-literal`} />
                            ) : null}
                          </div>
                        );
                      })}
                      <div className="text-[10px] text-muted-foreground">
                        Merge tags render per-lead from the lead&apos;s record. Pick &quot;Literal&quot; to hard-code a value that&apos;s the same for everyone.
                      </div>
                    </div>
                  ) : (matchedTpl && (
                    <div className="text-[10px] text-muted-foreground">No {`{{n}}`} placeholders in this template.</div>
                  ))}
                  {/* R135g · Header media upload (image / document / video templates) */}
                  {matchedTpl && needsHeaderMedia && (
                    <div className="mt-2" data-testid="bulk-wa-header-media-section">
                      <Label className="text-xs">
                        Template requires a {templateHeaderType.toLowerCase()} header *
                      </Label>
                      {/* R267m · Feb 2026 — Auto-fill chip. When we
                          have a proven-good URL (last success) or an
                          Interakt-approved sample, show a green ready
                          state and offer "Change" instead of forcing
                          a fresh upload. */}
                      {headerMediaUrl && mediaSource !== "user_upload" && mediaSource !== "none" && (
                        <div className="mt-1 border border-emerald-300 bg-emerald-50/60 p-2 text-[11px] flex items-center gap-2"
                             data-testid="bulk-wa-header-media-autofill">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-emerald-900">
                              {mediaSource === "last_success"
                                ? "Reusing last successful video"
                                : "Using Interakt-approved sample"}
                            </div>
                            <div className="text-[10px] text-emerald-800/80 truncate">
                              {mediaSource === "last_success" && mediaMeta?.at && (
                                <>Sent {_relTime(mediaMeta.at)}
                                  {mediaMeta.by_name ? ` by ${mediaMeta.by_name}` : ""} — proven to deliver</>
                              )}
                              {mediaSource === "interakt_sample" && (
                                <>Baked into the template at approval time — Meta-safe</>
                              )}
                            </div>
                          </div>
                          <button type="button"
                                  onClick={() => { setHeaderMediaUrl(""); setHeaderFilename(""); setMediaSource("none"); }}
                                  className="text-[10px] px-1.5 py-0.5 border border-emerald-400 text-emerald-900 hover:bg-emerald-100"
                                  data-testid="bulk-wa-header-media-change">
                            Change
                          </button>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <label className="cursor-pointer">
                          <input type="file"
                                 accept={templateHeaderType === "IMAGE" ? "image/*"
                                          : templateHeaderType === "VIDEO" ? "video/*"
                                          : ".pdf,.doc,.docx,.xlsx,.csv"}
                                 onChange={(e) => uploadHeaderMedia(e.target.files?.[0])}
                                 className="hidden"
                                 data-testid="bulk-wa-header-media-input" />
                          <span className={`inline-block px-3 py-1.5 border border-border text-xs rounded-none ${uploadingMedia ? "opacity-50" : "hover:bg-muted"}`}>
                            {uploadingMedia ? "Uploading…" : (headerMediaUrl ? "Replace file" : `+ Upload ${templateHeaderType.toLowerCase()}`)}
                          </span>
                        </label>
                        {headerMediaUrl && mediaSource === "user_upload" && (
                          <span className="text-[11px] text-emerald-700 truncate max-w-[240px]"
                                data-testid="bulk-wa-header-media-filename">
                            ✓ {headerFilename || "attachment"}
                          </span>
                        )}
                      </div>
                      {headerMediaUrl && templateHeaderType === "IMAGE" && (
                        <img src={headerMediaUrl} alt="preview"
                             className="mt-2 max-h-32 border border-border object-cover"
                             data-testid="bulk-wa-header-media-preview" />
                      )}
                      <div className="text-[10px] text-muted-foreground mt-1">
                        The same {templateHeaderType.toLowerCase()} rides along with every recipient (Interakt fetches the public URL).
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">Message text *</Label>
                    <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} className="rounded-none" placeholder="Hi! Just checking in about your recent enquiry — happy to hop on a quick call this week?" data-testid="bulk-wa-text" />
                  </div>
                  <label className="flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="bulk-wa-force" />
                    Force send (ignore 24h window — may fail if lead hasn&apos;t replied recently)
                  </label>
                  <div className="text-[10px] text-amber-600">WhatsApp restricts free-text messages to contacts who replied within 24 hours. Otherwise, use a template.</div>
                </div>
              )}
              <div>
                <Label className="text-xs">Campaign label (optional)</Label>
                <Input value={label} onChange={(e) => setLabel(e.target.value)}
                       placeholder="E.g. Q1 reactivation blast"
                       className="rounded-none h-9" data-testid="bulk-wa-label" />
              </div>
              {/* R133 · Stagger between sends */}
              <div className="pt-2 border-t border-border grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Stagger (sec)</Label>
                  <Input type="number" min="0" step="0.5" value={intervalSec}
                         onChange={(e) => setIntervalSec(Number(e.target.value) || 0)}
                         className="rounded-none" data-testid="bulk-wa-interval" />
                  <div className="text-[10px] text-muted-foreground mt-1">
                    0 = burst. 2s ≈ 30/min. 6s ≈ 10/min.
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Send window (recipient local)</Label>
                  <Input value={sendWindow} onChange={(e) => setSendWindow(e.target.value)}
                         placeholder="09:00-18:00" className="rounded-none"
                         data-testid="bulk-wa-window" />
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Hold each send until recipient&apos;s local time falls inside this window. Empty = fire immediately.
                  </div>
                </div>
              </div>
              {sendWindow && (
                <div>
                  <Label className="text-xs">Fallback timezone (if lead has none)</Label>
                  <Input value={defaultTz} onChange={(e) => setDefaultTz(e.target.value)}
                         placeholder="Asia/Kolkata"
                         className="rounded-none h-9" data-testid="bulk-wa-default-tz" />
                </div>
              )}
              {/* R135g · Scheduled send */}
              <div>
                <Label className="text-xs">Schedule for later (optional)</Label>
                <Input type="datetime-local" value={waScheduledAt}
                       onChange={(e) => setWaScheduledAt(e.target.value)}
                       className="rounded-none" data-testid="bulk-wa-schedule" />
                <div className="text-[10px] text-muted-foreground mt-1">
                  Blank = fire now. Set a future date+time to queue until then.
                </div>
              </div>
            </>
          )}
          {/* R135c · Recipient preview */}
          {!campaign && showPreview && preview && (
            <div className="border border-primary/40 bg-primary/5 p-2 space-y-2"
                 data-testid="bulk-wa-preview">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-primary">
                <span>Recipient preview — first {preview.length}</span>
                <button type="button" onClick={() => setShowPreview(false)}
                        className="underline">Close preview</button>
              </div>
              {preview.map((lead, i) => {
                const rendered = varSlots.map((v) => renderMergeTags(v, lead));
                const previewBody = mode === "template"
                  ? `[${templateName || "template"}]` + (rendered.length ? ` · ${rendered.filter(Boolean).join(" · ")}` : "")
                  : renderMergeTags(text, lead);
                return (
                  <div key={i} className="border border-primary/20 bg-white p-2 text-xs">
                    <div className="font-semibold">{lead.name || "—"} · {lead.phone || "no phone"}</div>
                    <div className="text-muted-foreground mt-1 whitespace-pre-wrap">{previewBody}</div>
                  </div>
                );
              })}
            </div>
          )}
          {/* R267b · Feb 2026 — Preflight validation card. Groups
              leads by phone status and offers a one-tap "Send
              anyway" override for genuinely off-panel numbers. */}
          {!campaign && preflight && (preflight.counts?.missing || preflight.counts?.invalid || preflight.counts?.no_country_code) > 0 && (
            <div className="border border-amber-300 bg-amber-50 p-2 space-y-2"
                 data-testid="bulk-wa-preflight">
              <div className="flex items-center gap-2 text-xs">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-700 shrink-0" />
                <span className="font-semibold text-amber-900">
                  Recipient issues detected
                </span>
                <span className="ml-auto font-mono tabular-nums text-[10px] text-amber-800">
                  {preflight.total - (preflight.counts?.ok || 0)} of {preflight.total} may not deliver
                </span>
              </div>
              {["missing", "no_country_code", "invalid"].map((bucket) => {
                const rows = preflight.leads_by_status?.[bucket] || [];
                if (!rows.length) return null;
                const label = bucket === "missing" ? "No phone" :
                              bucket === "no_country_code" ? "Missing country code" :
                              "Invalid";
                return (
                  <div key={bucket} className="border border-amber-200 bg-white p-1.5"
                       data-testid={`bulk-wa-preflight-${bucket}`}>
                    <div className="flex items-center gap-2">
                      <div className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold flex-1">
                        {label} · {rows.length}
                      </div>
                      {/* R267c · One-click auto-fixer for the
                          "no country code" bucket. Uses the modal
                          country-code input (default +91). */}
                      {bucket === "no_country_code" && (
                        <PrependCountryCodeButton
                          wsId={wsId}
                          rows={rows}
                          onDone={() => runPreflight({ silent: true })}
                        />
                      )}
                    </div>
                    <ul className="mt-1 text-[11px] space-y-0.5 max-h-24 overflow-auto">
                      {rows.slice(0, 8).map((r) => (
                        <li key={r.lead_id} className="flex items-center gap-1.5">
                          <span className="font-medium truncate max-w-[180px]">{r.name || "—"}</span>
                          <span className="text-neutral-500 font-mono">{r.phone || "∅"}</span>
                          {r.reason && (
                            <span className="text-amber-700 truncate text-[10px]" title={r.reason}>
                              · {r.reason}
                            </span>
                          )}
                        </li>
                      ))}
                      {rows.length > 8 && (
                        <li className="text-[10px] text-neutral-500">
                          + {rows.length - 8} more…
                        </li>
                      )}
                    </ul>
                  </div>
                );
              })}
              <div className="flex items-center justify-between gap-2 pt-1 border-t border-amber-200">
                <label className="flex items-center gap-1.5 text-[11px] text-amber-900">
                  <input type="checkbox"
                         checked={preflightOverride}
                         onChange={(e) => setPreflightOverride(e.target.checked)}
                         data-testid="bulk-wa-preflight-override" />
                  Send anyway — I understand these may not deliver
                </label>
                <button type="button"
                        className="text-[10px] underline text-amber-900 disabled:opacity-50"
                        disabled={preflightBusy}
                        onClick={() => runPreflight({ silent: false })}
                        data-testid="bulk-wa-preflight-refresh">
                  {preflightBusy ? "checking…" : "re-check"}
                </button>
              </div>
            </div>
          )}
          {!campaign && preflight && !((preflight.counts?.missing || preflight.counts?.invalid || preflight.counts?.no_country_code) > 0) && (
            <div className="border border-emerald-300 bg-emerald-50 p-1.5 text-[11px] text-emerald-900 flex items-center gap-2"
                 data-testid="bulk-wa-preflight-clean">
              <CheckCircle2 className="w-3 h-3" />
              <span className="font-semibold">All {preflight.total} recipient{preflight.total === 1 ? "" : "s"} valid.</span>
            </div>
          )}
          {/* R135e · Rate-limit advisory */}
          {!campaign && waRateWarning && (
            <div className={`border p-2 text-xs ${
              waRateWarning.level === "danger" ? "border-rose-300 bg-rose-50 text-rose-800" :
              waRateWarning.level === "warn" ? "border-amber-300 bg-amber-50 text-amber-800" :
              "border-emerald-300 bg-emerald-50 text-emerald-800"
            }`} data-testid="bulk-wa-rate-warning">
              <div className="font-semibold flex items-center gap-2">
                <span>{waRateWarning.level === "ok" ? "Rate check" : "Rate limit warning"}</span>
                <span className="ml-auto text-[10px] uppercase tracking-widest">{waRateWarning.level}</span>
              </div>
              <div className="mt-0.5">{waRateWarning.msg}</div>
            </div>
          )}
          {campaign && (
            <div className="border border-emerald-300 bg-emerald-50 p-2 space-y-1"
                 data-testid="bulk-wa-campaign-progress">
              <div className="flex items-center gap-2 text-xs">
                <Sparkles className="w-3 h-3 text-emerald-700" />
                <span className="font-semibold">Campaign · {campaign.state}</span>
                {/* R267b · "Resumed after restart" badge — surfaces
                    the recovery event so ops don't refresh the page
                    thinking the send stalled. */}
                {campaign.resume_note && (
                  <span className="text-[9px] font-semibold uppercase tracking-widest px-1 py-[1px] bg-blue-500/10 text-blue-700 border border-blue-500/40"
                        data-testid="bulk-wa-resumed-badge"
                        title={campaign.resume_note}>
                    Resumed
                  </span>
                )}
                <span className="ml-auto font-mono tabular-nums text-emerald-800">
                  {campaign.processed || 0} / {campaign.total || 0} · sent {campaign.sent || 0}
                  {campaign.failed ? ` · failed ${campaign.failed}` : ""}
                  {campaign.skipped ? ` · skipped ${campaign.skipped}` : ""}
                </span>
              </div>
              <div className="h-2 bg-emerald-200">
                <div className={`h-2 transition-all ${campaign.state === "done" ? "bg-emerald-600" : "bg-emerald-500"}`}
                     style={{ width: `${pct}%` }}
                     data-testid="bulk-wa-progress-bar" />
              </div>
              {/* R267b · Delivered / Read chips from Interakt
                  webhook. Only show once we have any signal, so
                  demo/dry runs stay clean. */}
              {(campaign.delivered > 0 || campaign.read > 0 || campaign.delivery_failed > 0) && (
                <div className="flex items-center gap-2 text-[10px] pt-1 border-t border-emerald-200"
                     data-testid="bulk-wa-delivery-chips">
                  <span className="uppercase tracking-widest text-emerald-800/70 font-semibold">Delivery</span>
                  {campaign.delivered > 0 && (
                    <span className="font-mono text-emerald-800"
                          data-testid="bulk-wa-delivered-count">
                      ✓ delivered {campaign.delivered}
                    </span>
                  )}
                  {campaign.read > 0 && (
                    <span className="font-mono text-blue-700"
                          data-testid="bulk-wa-read-count">
                      ✓✓ read {campaign.read}
                    </span>
                  )}
                  {campaign.delivery_failed > 0 && (
                    <span className="font-mono text-rose-700"
                          data-testid="bulk-wa-delivery-failed-count">
                      ✗ meta-rejected {campaign.delivery_failed}
                    </span>
                  )}
                </div>
              )}
              {/* R267b · Retry Failed button once the campaign is
                  done. Fires a fresh campaign against only the
                  failed lead ids. */}
              {campaign.state === "done" && campaign.failed > 0 && (
                <div className="flex items-center gap-2 pt-1 border-t border-emerald-200">
                  <button type="button"
                          className="text-[11px] underline text-rose-700 hover:text-rose-900 disabled:opacity-50"
                          disabled={busy}
                          onClick={async () => {
                            if (!window.confirm(`Re-send only the ${campaign.failed} failed contact(s)? A new campaign will be created.`)) return;
                            setBusy(true);
                            try {
                              const { data } = await api.post(
                                `/workspaces/${wsId}/sales/campaigns/${campaign.campaign_id}/retry-failed-wa`);
                              toast.success(`Retry campaign queued · ${data.total} recipients`);
                              setCampaign({ campaign_id: data.campaign_id,
                                             total: data.total,
                                             state: "queued", sent: 0,
                                             failed: 0, skipped: 0, processed: 0 });
                            } catch (e) {
                              toast.error(apiErrorMessage(e, "Retry failed"));
                            } finally { setBusy(false); }
                          }}
                          data-testid="bulk-wa-retry-failed-btn">
                    ↻ Re-send only failed ({campaign.failed})
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="px-5 py-3 shrink-0 border-t border-border bg-background">
          {campaign ? (
            <Button className="rounded-none" onClick={onClose}
                    data-testid="bulk-wa-close">Close</Button>
          ) : (
            <>
              <Button variant="outline" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button variant="outline" className="rounded-none" onClick={loadPreview}
                      disabled={busy} data-testid="bulk-wa-preview-btn">
                Preview
              </Button>
              <Button className="rounded-none" onClick={send} disabled={busy} data-testid="bulk-wa-send">
                {busy ? <><Sparkles className="w-4 h-4 mr-1 animate-pulse" /> Queuing…</> : <><Send className="w-4 h-4 mr-1" /> Start campaign</>}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// R133 · Feb 2026 — Bulk Email blast to selected leads / segment.
// Kicks off a background campaign and polls for progress like Mailchimp.
export function BulkEmailDialog({ leadIds, wsId, segmentId, onClose, onDone }) {
  const [mailboxes, setMailboxes] = useState([]);
  const [mailboxId, setMailboxId] = useState("");
  // R135b · Email template picker + signature preview.
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [signaturePreview, setSignaturePreview] = useState("");
  const [subject, setSubject] = useState("");
  const [subjectB, setSubjectB] = useState("");        // R135d · A/B subject test
  const [showSubjectB, setShowSubjectB] = useState(false);
  const [bodyHtml, setBodyHtml] = useState(
    "<p>Hi {{first_name}},</p>\n<p></p>\n<p>—</p>");
  const [intervalSec, setIntervalSec] = useState(5);
  const [label, setLabel] = useState("");
  // R135 · Send Time Optimizer
  const [sendWindow, setSendWindow] = useState("");    // "09:00-18:00"
  const [defaultTz, setDefaultTz] = useState("");
  // R135c · Attachments (list of {file_id, filename, size}).
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  // R135d · Recent attachments for one-click reuse.
  const [recentAttachments, setRecentAttachments] = useState([]);
  // R135c · Save as template + preview.
  const [savingTpl, setSavingTpl] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(0);     // R135d · cycle through preview batches
  // R135f · Suppression preflight
  const [suppressionCount, setSuppressionCount] = useState(0);
  const [suppressionTotal, setSuppressionTotal] = useState(0);
  const [suppressionSample, setSuppressionSample] = useState([]);
  const [suppressionByReason, setSuppressionByReason] = useState({});
  // R135g · Scheduled send (ISO string) + domain-auth for picked mailbox.
  const [scheduledAt, setScheduledAt] = useState("");
  const [domainAuth, setDomainAuth] = useState(null);
  // R135h · Team Sender Rotation + AI Subject Coach.
  const [rotationIds, setRotationIds] = useState([]);
  const [coachBusy, setCoachBusy] = useState(false);
  const [coachSuggestions, setCoachSuggestions] = useState([]);
  const [coachTone, setCoachTone] = useState("professional");
  const [busy, setBusy] = useState(false);
  const [campaign, setCampaign] = useState(null);

  // R138bw · Reply-To toggle — where should replies land when sending
  // via the workspace System Default (Zepto/Resend)? Persist per user
  // in localStorage so power-users don't re-pick every time.
  const REPLY_TO_KEY = "bulk-email-reply-to";
  const [replyToTarget, setReplyToTarget] = useState(() => {
    try {
      const v = localStorage.getItem(REPLY_TO_KEY);
      if (v === "me" || v === "workspace") return v;
    } catch { /* ignore */ }
    return "me";
  });
  useEffect(() => {
    try { localStorage.setItem(REPLY_TO_KEY, replyToTarget); } catch { /* ignore */ }
  }, [replyToTarget]);

  // R138bw · System-sender warmth — advisory chip when using System
  // Default so we don't blast fresh workspaces to a spam folder.
  const [systemWarmth, setSystemWarmth] = useState(null);

  // R135d · Auto-save drafts to localStorage keyed by workspace + target.
  const draftKey = `bulk-email-draft:${wsId}:${segmentId || (leadIds?.slice().sort().join(",") || "none")}`;

  const targetCount = leadIds?.length || 0;

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/workspaces/${wsId}/mailboxes`);
        const rows = Array.isArray(r.data) ? r.data : (r.data?.rows || []);
        setMailboxes(rows);
        const primary = rows.find((m) => m.is_default) || rows[0];
        if (primary) setMailboxId(primary.id);
      } catch (e) { toast.error(apiErrorMessage(e, "Failed to load mailboxes")); }
      // Templates (reuses marketing_templates scoped for channel=email).
      try {
        const r = await api.get(`/workspaces/${wsId}/email-templates`);
        // R155 · Feb 2026 — Accept BOTH the old `templates` shape (legacy
        // marketing_templates endpoint) and the new `rows` shape from
        // routes_email_templates.py so PMs see every reusable template
        // in one picker.
        setTemplates(r.data?.rows || r.data?.templates || []);
      } catch { /* silent */ }
      // Signature preview (user > workspace fallback). Server-side we
      // combine mailbox > user > workspace at send-time; the preview
      // shows whichever is likeliest to be appended.
      try {
        const r1 = await api.get(`/me/email-signature`);
        const userSig = (r1.data?.signature_html || "").trim();
        if (userSig) { setSignaturePreview(userSig); return; }
      } catch { /* fall through */ }
      try {
        const r2 = await api.get(`/workspaces/${wsId}/email-signature`);
        setSignaturePreview((r2.data?.signature_html || "").trim());
      } catch { /* silent */ }
    })();
    // R135d · Recent mail-attachments for one-click reuse.
    (async () => {
      try {
        const r = await api.get(`/workspaces/${wsId}/files?scope=mail_attachment&limit=20`);
        setRecentAttachments(r.data?.rows || []);
      } catch { /* silent */ }
    })();
    // R138bw · Prefetch System Default sender warmth so the
    // deliverability chip renders instantly when the user picks it.
    (async () => {
      try {
        const r = await api.get(`/workspaces/${wsId}/leads/bulk-email/system-warmth`);
        setSystemWarmth(r.data || null);
      } catch { /* silent — endpoint optional */ }
    })();
    // R138ai · Suppression preflight — warn how many recipients will be
    // auto-skipped so users don't waste sending reputation on dead
    // addresses. Uses `POST /sales/suppressions/check`.
    (async () => {
      try {
        const r = await api.post(`/workspaces/${wsId}/sales/suppressions/check`, {
          ...(leadIds?.length ? { lead_ids: leadIds } : { segment_id: segmentId }),
        });
        setSuppressionCount(r.data?.suppressed_count || 0);
        setSuppressionTotal(r.data?.total_recipients || 0);
        setSuppressionSample(r.data?.suppressed_emails || []);
        setSuppressionByReason(r.data?.by_reason || {});
      } catch { /* silent */ }
    })();
    // R135d · Restore draft from localStorage. Seed key wins over
    // the per-target draft (that's what "Duplicate campaign" sets).
    try {
      const seedRaw = localStorage.getItem(`bulk-email-draft:${wsId}:seed`);
      const raw = seedRaw || localStorage.getItem(draftKey);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.subject) setSubject(d.subject);
        if (d.subjectB) { setSubjectB(d.subjectB); setShowSubjectB(true); }
        if (d.bodyHtml) setBodyHtml(d.bodyHtml);
        if (d.label) setLabel(d.label);
        if (typeof d.intervalSec === "number") setIntervalSec(d.intervalSec);
        if (d.sendWindow) setSendWindow(d.sendWindow);
        if (d.defaultTz) setDefaultTz(d.defaultTz);
        if (Array.isArray(d.attachments)) setAttachments(d.attachments);
        // Seed is one-shot — clear it once consumed so it doesn't
        // shadow the user's next fresh dialog.
        if (seedRaw) {
          try { localStorage.removeItem(`bulk-email-draft:${wsId}:seed`); } catch { /* silent */ }
          toast.info("Loaded duplicated campaign — review + hit Start");
        }
      }
    } catch { /* silent */ }
  }, [wsId, draftKey]);

  // R135d · Debounced auto-save on any composition change.
  useEffect(() => {
    if (campaign) return undefined;   // stop saving once queued
    const t = setTimeout(() => {
      try {
        localStorage.setItem(draftKey, JSON.stringify({
          subject, subjectB, bodyHtml, label, intervalSec,
          sendWindow, defaultTz, attachments,
          savedAt: Date.now(),
        }));
      } catch { /* localStorage may be full or disabled */ }
    }, 800);
    return () => clearTimeout(t);
  }, [draftKey, subject, subjectB, bodyHtml, label, intervalSec,
        sendWindow, defaultTz, attachments, campaign]);

  // R135g · Fetch SPF/DKIM/DMARC status when the mailbox choice changes.
  useEffect(() => {
    if (!mailboxId || !wsId) { setDomainAuth(null); return; }
    (async () => {
      try {
        const r = await api.get(`/workspaces/${wsId}/sales/mailbox/${mailboxId}/domain-auth`);
        setDomainAuth(r.data || null);
      } catch { setDomainAuth(null); }
    })();
  }, [mailboxId, wsId]);

  const applyTemplate = (tid) => {
    setTemplateId(tid);
    if (!tid) return;
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    // R155 · Feb 2026 — Legacy marketing templates use `body`, new
    // custom templates use `body_html`. Pick whichever exists.
    if (t.subject) setSubject(t.subject);
    const html = t.body_html || t.body || "";
    if (html) setBodyHtml(html);
    toast.success(`Applied template · ${t.name || "untitled"}`);
  };

  // R135c · Upload an attachment via the shared /files/upload endpoint.
  const onAttachmentPick = async (file) => {
    if (!file) return;
    if (attachments.length >= 10) {
      toast.error("Max 10 attachments per campaign");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/workspaces/${wsId}/files/upload?scope=mail_attachment`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((prev) => [...prev, {
        file_id: data.id,
        filename: data.original_filename || file.name,
        size: data.size || file.size,
      }]);
      toast.success(`Attached ${data.original_filename || file.name}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Upload failed")); }
    finally { setUploading(false); }
  };

  const removeAttachment = (fid) => {
    setAttachments((prev) => prev.filter((a) => a.file_id !== fid));
  };

  // R135c · Save the current subject + body as a reusable template.
  const saveAsTemplate = async () => {
    const name = window.prompt("Template name:");
    if (!name || !name.trim()) return;
    setSavingTpl(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/marketing/templates`, {
        name: name.trim(),
        channel: "email",
        subject: subject.trim(),
        body: bodyHtml,
      });
      setTemplates((prev) => [data, ...prev]);
      setTemplateId(data.id);
      toast.success(`Saved template "${name.trim()}"`);
    } catch (e) { toast.error(apiErrorMessage(e, "Save template failed")); }
    finally { setSavingTpl(false); }
  };

  // R135c · Recipient preview — fetch up to 12 leads for cycling.
  const loadPreview = async () => {
    try {
      const r = await api.get(`/workspaces/${wsId}/leads`);
      const all = r.data || [];
      let previewLeads = [];
      if (leadIds?.length) {
        const idSet = new Set(leadIds);
        previewLeads = all.filter((l) => idSet.has(l.id));
      } else {
        // Segment mode: pull a random-ish sample of up to 12.
        previewLeads = [...all].sort(() => Math.random() - 0.5);
      }
      setPreview(previewLeads.slice(0, 12));
      setPreviewIdx(0);
      setShowPreview(true);
    } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); }
  };

  // Poll campaign progress every 2s while running.
  useEffect(() => {
    if (!campaign?.campaign_id || campaign.state === "done") return undefined;
    const t = setInterval(async () => {
      try {
        const r = await api.get(
          `/workspaces/${wsId}/sales/campaigns/${campaign.campaign_id}`);
        setCampaign((prev) => ({ ...prev, ...r.data }));
        if (r.data.state === "done") {
          toast.success(`Campaign complete · ${r.data.sent} sent · ${r.data.failed} failed`);
          onDone?.();
        }
      } catch { /* keep polling */ }
    }, 2000);
    return () => clearInterval(t);
  }, [campaign?.campaign_id, campaign?.state, wsId, onDone]);

  const start = async () => {
    if (targetCount === 0 && !segmentId) return toast.error("No leads or segment selected");
    // R138bt · mailboxId="" means "System default" — allowed.
    if (!subject.trim()) return toast.error("Subject required");
    if (!bodyHtml.trim()) return toast.error("Body required");
    if (!window.confirm(
      `Send email to ${segmentId ? "the segment" : `${targetCount} lead(s)`}?` +
      ` One email per lead, staggered ${intervalSec}s apart.` +
      (sendWindow ? ` Held to recipient's ${sendWindow} local window.` : ""))) return;
    setBusy(true);
    try {
      const payload = {
        ...(leadIds?.length ? { lead_ids: leadIds } : { segment_id: segmentId }),
        mailbox_id: mailboxId || null,   // null → System default
        subject: subject.trim(),
        subject_b: showSubjectB && subjectB.trim() ? subjectB.trim() : null,
        body_html: bodyHtml, interval_seconds: intervalSec,
        campaign_label: label.trim() || null,
        send_window_local: sendWindow.trim() || null,
        default_timezone: defaultTz.trim() || null,
        attachment_file_ids: attachments.map((a) => a.file_id),
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        rotation_mailbox_ids: rotationIds.length ? rotationIds : null,
        // R138bw · Reply-To routing — only meaningful when mailbox_id is
        // null (System Default). Kept in the payload always so the
        // campaign record captures the sender's intent.
        reply_to_target: replyToTarget,
      };
      const { data } = await api.post(
        `/workspaces/${wsId}/leads/bulk-email-send`, payload);
      if (!data.campaign_id) {
        toast.info("No matching recipients");
        onClose?.();
        return;
      }
      setCampaign({ campaign_id: data.campaign_id, total: data.total,
                     state: "starting", sent: 0, failed: 0, skipped: 0, processed: 0 });
      toast.success(`Campaign queued · ${data.total} recipients`);
      // R135d · Clear the auto-saved draft — the blast has been queued.
      try { localStorage.removeItem(draftKey); } catch { /* silent */ }
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk email failed")); }
    finally { setBusy(false); }
  };

  // R138bx · Batch Splitter — when the recipient count exceeds the
  // deliverability cap, split lead_ids into N chunks of ≤ cap each and
  // queue N campaigns 24h apart. Segment-based blasts can't be split
  // client-side (server resolves the segment), so the button is hidden
  // in that case.
  const canBatchSplit = !!(leadIds?.length && systemWarmth && !mailboxId
                            && targetCount > (systemWarmth.cap || 0));
  const batchCount = canBatchSplit
    ? Math.ceil(targetCount / (systemWarmth.cap || 1))
    : 0;
  const startBatched = async () => {
    if (!canBatchSplit) return;
    if (!subject.trim()) return toast.error("Subject required");
    if (!bodyHtml.trim()) return toast.error("Body required");
    if (!window.confirm(
      `Split ${targetCount} recipients into ${batchCount} batches of ≤ ${systemWarmth.cap}` +
      ` and schedule 24h apart? First batch starts now.`)) return;
    setBusy(true);
    const cap = systemWarmth.cap;
    const chunks = [];
    for (let i = 0; i < leadIds.length; i += cap) chunks.push(leadIds.slice(i, i + cap));
    let queued = 0;
    let failed = 0;
    let firstCampaignId = null;
    let firstTotal = 0;
    for (let idx = 0; idx < chunks.length; idx += 1) {
      const chunk = chunks[idx];
      const when = idx === 0
        ? null   // send now
        : new Date(Date.now() + idx * 24 * 60 * 60 * 1000).toISOString();
      const suffix = ` · batch ${idx + 1}/${chunks.length}`;
      const payload = {
        lead_ids: chunk,
        mailbox_id: mailboxId || null,
        subject: subject.trim(),
        subject_b: showSubjectB && subjectB.trim() ? subjectB.trim() : null,
        body_html: bodyHtml,
        interval_seconds: intervalSec,
        campaign_label: (label.trim() || `Bulk email · ${new Date().toISOString().slice(0, 10)}`) + suffix,
        send_window_local: sendWindow.trim() || null,
        default_timezone: defaultTz.trim() || null,
        attachment_file_ids: attachments.map((a) => a.file_id),
        scheduled_at: when,
        rotation_mailbox_ids: rotationIds.length ? rotationIds : null,
        reply_to_target: replyToTarget,
      };
      try {
        const { data } = await api.post(
          `/workspaces/${wsId}/leads/bulk-email-send`, payload);
        queued += (data.total || 0);
        if (!firstCampaignId && data.campaign_id) {
          firstCampaignId = data.campaign_id;
          firstTotal = data.total || 0;
        }
      } catch (e) {
        failed += 1;
        toast.error(apiErrorMessage(e, `Batch ${idx + 1} failed to queue`));
      }
    }
    if (queued > 0) {
      toast.success(
        `Queued ${chunks.length - failed}/${chunks.length} batches · ${queued} total recipients ` +
        `· first batch now, next batches 24h apart`);
      if (firstCampaignId) {
        setCampaign({ campaign_id: firstCampaignId, total: firstTotal,
                       state: "starting", sent: 0, failed: 0, skipped: 0, processed: 0 });
      }
      try { localStorage.removeItem(draftKey); } catch { /* silent */ }
    }
    setBusy(false);
  };

  const pct = campaign?.total > 0
    ? Math.min(100, Math.round((campaign.processed / campaign.total) * 100))
    : 0;
  // R135h · AI Subject Coach — generate 3 subject candidates.
  const runSubjectCoach = async () => {
    if (!bodyHtml || bodyHtml.trim().length < 30) {
      toast.error("Write at least a few sentences of body before asking AI");
      return;
    }
    setCoachBusy(true);
    try {
      const r = await api.post(
        `/workspaces/${wsId}/sales/ai/subject-coach`, {
          body_html: bodyHtml, tone: coachTone,
          existing_subject: subject.trim() || null,
        });
      setCoachSuggestions(r.data?.subjects || []);
      if (!(r.data?.subjects || []).length) toast.info("AI returned no suggestions");
    } catch (e) { toast.error(apiErrorMessage(e, "AI coach failed")); }
    finally { setCoachBusy(false); }
  };


  // R135e · Live rate-limit + deliverability advisory (email dialog).
  const effectiveTargetCount = leadIds?.length || 0;
  const rateWarning = computeRateWarning(intervalSec, effectiveTargetCount);
  const deliverability = useMemo(
    () => computeDeliverabilityScore({
      subject, bodyHtml, signature: signaturePreview,
      attachmentsCount: attachments.length,
    }),
    [subject, bodyHtml, signaturePreview, attachments.length]);


  // R138z · Feb 2026 — Collapsible advanced sections so directors aren't
  // scrolling past 200+px of dormant Attachments / Team Rotation / AI Coach /
  // Preview blocks. Persist state in localStorage per workspace.
  const [openSections, setOpenSections] = useState(() => {
    try {
      const raw = localStorage.getItem(`bulk-email-adv-open:${wsId}`);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    // First-run defaults: all collapsed for less visual noise.
    return { schedule: false, attachments: false, rotation: false, coach: false, preview: false };
  });
  const toggleSection = (k) => {
    setOpenSections((s) => {
      const next = { ...s, [k]: !s[k] };
      try { localStorage.setItem(`bulk-email-adv-open:${wsId}`, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-6xl w-[94vw] max-h-[92vh] p-0 gap-0 rounded-none overflow-hidden flex flex-col"
        data-testid="bulk-email-dialog"
      >
        <DialogHeader className="px-5 py-3 border-b border-border flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-primary" /> Bulk email · {segmentId ? "segment" : `${targetCount} lead(s)`}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-5 py-4">
        <div className="space-y-3" data-testid="bulk-email-body">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Send from mailbox</Label>
              <Select value={mailboxId || "__system__"} onValueChange={(v) => setMailboxId(v === "__system__" ? "" : v)}>
                <SelectTrigger className="rounded-none" data-testid="bulk-email-mailbox">
                  <SelectValue placeholder="Pick a mailbox" />
                </SelectTrigger>
                <SelectContent>
                  {/* R138bt · System default routes via workspace's Zepto/
                       Resend provider — same option available on
                       individual lead sends. Handy when no personal
                       mailbox is wired up yet. */}
                  <SelectItem value="__system__" data-testid="bulk-email-mailbox-system-default">
                    <span className="flex items-center gap-2">
                      <span className="font-semibold">System default</span>
                      <span className="text-[9px] px-1 py-0.5 border border-primary/40 text-primary bg-primary/5">
                        Zepto · workspace provider
                      </span>
                    </span>
                  </SelectItem>
                  {mailboxes.map((m) => {
                    const w = computeWarmupBadge(m);
                    // R138c · Feb 2026 — Mailbox model uses `name`; older
                    // rows may only have host/username. Fall through many
                    // fields so the trigger never leaks a UUID.
                    const label = m.name || m.display_name || m.smtp_from_email || m.email
                                   || m.smtp_username || m.imap_username
                                   || m.smtp_host || m.imap_host
                                   || `Mailbox ${(m.id || "").slice(0, 8)}…`;
                    return (
                      <SelectItem key={m.id} value={m.id}>
                        <span className="flex items-center gap-2">
                          <span>{label}</span>
                          {w && (
                            <span className={`text-[9px] px-1 py-0.5 border ${
                              w.level === "danger" ? "border-rose-300 text-rose-700 bg-rose-50" :
                              w.level === "warn" ? "border-amber-300 text-amber-700 bg-amber-50" :
                              "border-emerald-300 text-emerald-700 bg-emerald-50"
                            }`}>{w.label}</span>
                          )}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {/* R138bv · System-default advisory — clarifies where
                  replies route based on the Reply-To toggle below. */}
              {!mailboxId && (
                <div className="mt-1 space-y-1"
                     data-testid="bulk-email-system-advisory">
                  <div className="text-[10px] text-primary bg-primary/5 border border-primary/30 px-2 py-1">
                    Sent via workspace Zepto sender with
                    <span className="font-semibold"> your name</span>
                    {replyToTarget === "me" ? (
                      <> and <span className="font-semibold">your email as Reply-To</span> — replies land in your inbox.</>
                    ) : (
                      <> — Reply-To falls back to the workspace/provider address so replies pool into the shared inbox.</>
                    )}
                  </div>
                  {/* R138bw · Reply-To toggle — power-users route replies
                      to their inbox OR to the shared workspace inbox. */}
                  <div className="flex items-center gap-1 text-[10px]"
                       data-testid="bulk-email-reply-to-toggle">
                    <span className="uppercase tracking-widest text-muted-foreground mr-1">Reply-To</span>
                    <button type="button"
                            onClick={() => setReplyToTarget("me")}
                            className={`px-2 py-0.5 border ${replyToTarget === "me" ? "border-primary bg-primary/10 text-primary font-semibold" : "border-border hover:bg-muted"}`}
                            data-testid="bulk-email-reply-to-me">
                      Me · personal inbox
                    </button>
                    <button type="button"
                            onClick={() => setReplyToTarget("workspace")}
                            className={`px-2 py-0.5 border ${replyToTarget === "workspace" ? "border-primary bg-primary/10 text-primary font-semibold" : "border-border hover:bg-muted"}`}
                            data-testid="bulk-email-reply-to-workspace">
                      Workspace · shared inbox
                    </button>
                  </div>
                  {/* R138bw · Deliverability tips chip — shows Zepto
                      domain warmth heuristic + recommended recipient
                      cap so fresh workspaces don't nuke sender rep. */}
                  {systemWarmth && (() => {
                    const lvl = systemWarmth.level;
                    const cls = lvl === "cold"
                      ? "border-rose-300 text-rose-800 bg-rose-50"
                      : lvl === "warming"
                      ? "border-amber-300 text-amber-800 bg-amber-50"
                      : "border-emerald-300 text-emerald-800 bg-emerald-50";
                    const capBreach = targetCount > (systemWarmth.cap || 0);
                    return (
                      <div className={`text-[10px] px-2 py-1 border ${cls}`}
                           data-testid="bulk-email-deliverability-chip">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold uppercase tracking-widest">
                            {systemWarmth.label || lvl}
                          </span>
                          <span>· recommended cap {systemWarmth.cap} recipients</span>
                          {typeof systemWarmth.workspace_age_days === "number" && (
                            <span>· workspace age {systemWarmth.workspace_age_days}d</span>
                          )}
                          {typeof systemWarmth.sent_last_30d_recipients === "number" && (
                            <span>· {systemWarmth.sent_last_30d_recipients} sent (30d)</span>
                          )}
                        </div>
                        <div className="mt-0.5">{systemWarmth.tip}</div>
                        {capBreach && targetCount > 0 && (
                          <div className="mt-1 flex items-center gap-2 flex-wrap"
                               data-testid="bulk-email-deliverability-capbreach">
                            <span className="font-semibold">
                              This blast targets {targetCount} — above the {systemWarmth.cap} recommendation.
                            </span>
                            {canBatchSplit && (
                              <button type="button"
                                      onClick={startBatched}
                                      disabled={busy}
                                      className="px-2 py-0.5 border border-primary bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50"
                                      data-testid="bulk-email-batch-split-btn">
                                Split into {batchCount} batches · 24h apart
                              </button>
                            )}
                            {!canBatchSplit && (
                              <span>Consider splitting into batches manually.</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
              {/* R135f · Warmup advisory when picked mailbox is fresh */}
              {(() => {
                const sel = mailboxes.find((m) => m.id === mailboxId);
                const w = computeWarmupBadge(sel);
                if (!w || w.level === "ok") return null;
                return (
                  <div className={`mt-1 text-[10px] ${w.level === "danger" ? "text-rose-700" : "text-amber-700"}`}
                       data-testid="bulk-email-mailbox-warmup">
                    Warmup: {w.label} — keep this campaign small ({w.level === "danger" ? "< 20" : "< 100"} recipients) to protect sender reputation.
                  </div>
                );
              })()}
              {/* R135g · Domain auth pills */}
              {domainAuth && domainAuth.domain && (
                <div className="mt-1 flex items-center gap-2 text-[10px]"
                     data-testid="bulk-email-domain-auth">
                  <span className="text-muted-foreground">{domainAuth.domain}:</span>
                  {["spf", "dkim", "dmarc"].map((k) => {
                    const v = domainAuth[k] || "unknown";
                    const cls = v === "ok" ? "border-emerald-300 text-emerald-700 bg-emerald-50" :
                                v === "weak" ? "border-amber-300 text-amber-700 bg-amber-50" :
                                v === "missing" ? "border-rose-300 text-rose-700 bg-rose-50" :
                                "border-neutral-300 text-neutral-600 bg-neutral-50";
                    return (
                      <span key={k} className={`px-1 py-0.5 border font-mono ${cls}`}>
                        {k.toUpperCase()} {v}
                      </span>
                    );
                  })}
                </div>
              )}
              {/* R135h · Team Sender Rotation */}
              {mailboxes.length > 1 && (
                <div className="mt-1" data-testid="bulk-email-rotation-section">
                  <div className="text-[10px] text-muted-foreground mb-1">
                    Team rotation · round-robin between multiple mailboxes on the same domain
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {mailboxes.filter((m) => m.id !== mailboxId).map((m) => {
                      const on = rotationIds.includes(m.id);
                      return (
                        <button key={m.id} type="button"
                                onClick={() => setRotationIds((prev) =>
                                  on ? prev.filter((x) => x !== m.id) : [...prev, m.id])}
                                className={`text-[10px] px-2 py-0.5 border ${on ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted"}`}
                                data-testid={`bulk-email-rotation-toggle-${m.id}`}>
                          {on ? "✓ " : "+ "}{m.name || m.display_name || m.smtp_from_email || m.email || m.smtp_username || m.imap_username || m.smtp_host || m.imap_host || `Mailbox ${(m.id || "").slice(0, 8)}…`}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Stagger (sec between sends)</Label>
              <Input type="number" min="0" step="0.5" value={intervalSec}
                     onChange={(e) => setIntervalSec(Number(e.target.value) || 0)}
                     className="rounded-none" data-testid="bulk-email-interval" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Campaign label (optional)</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)}
                   placeholder="E.g. October promo · trial invites"
                   className="rounded-none" data-testid="bulk-email-label" />
          </div>
          {/* R135b · Email template picker */}
          <div>
            <Label className="text-xs">Email template (optional)</Label>
            <Select value={templateId} onValueChange={applyTemplate}>
              <SelectTrigger className="rounded-none" data-testid="bulk-email-template">
                <SelectValue placeholder={templates.length ? "Pick a saved template…" : "No saved templates — build one in Comms Center"} />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    <span className="flex items-center gap-2">
                      <span className="font-medium">{t.name || "Untitled"}</span>
                      {t.subject && <span className="text-[10px] text-muted-foreground">· {t.subject.slice(0, 40)}</span>}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {templateId && (
              <button type="button"
                      onClick={() => setTemplateId("")}
                      className="mt-1 text-[10px] text-muted-foreground underline"
                      data-testid="bulk-email-template-clear">
                Clear template
              </button>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Subject{showSubjectB ? " A" : ""}</Label>
              <div className="flex items-center gap-2">
                <Select value={coachTone} onValueChange={setCoachTone}>
                  <SelectTrigger className="h-6 w-[120px] rounded-none text-[10px]"
                                  data-testid="bulk-email-coach-tone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="friendly">Friendly</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="curious">Curious</SelectItem>
                    <SelectItem value="playful">Playful</SelectItem>
                  </SelectContent>
                </Select>
                <button type="button" onClick={runSubjectCoach} disabled={coachBusy}
                        className="text-[10px] px-2 py-0.5 border border-primary/50 text-primary hover:bg-primary/10 inline-flex items-center gap-1"
                        data-testid="bulk-email-coach-btn">
                  <Wand2 className="w-3 h-3" /> {coachBusy ? "…" : "AI subjects"}
                </button>
                {!showSubjectB ? (
                  <button type="button"
                          onClick={() => setShowSubjectB(true)}
                          className="text-[10px] underline text-primary"
                          data-testid="bulk-email-subject-b-add">
                    + A/B
                  </button>
                ) : (
                  <button type="button"
                          onClick={() => { setShowSubjectB(false); setSubjectB(""); }}
                          className="text-[10px] underline text-muted-foreground"
                          data-testid="bulk-email-subject-b-remove">
                    Remove B
                  </button>
                )}
              </div>
            </div>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                   placeholder="Hi {{first_name}}, exclusive offer inside"
                   className="rounded-none" data-testid="bulk-email-subject" />
            {(() => {
              const h = computeSubjectHint(subject);
              return (
                <div className={`text-[10px] mt-1 flex items-center gap-2 ${h.tone}`}
                     data-testid="bulk-email-subject-hint">
                  <span className="font-mono">{h.len} chars</span>
                  <span>· {h.msg}</span>
                  {h.len > 40 && (
                    <span className="ml-auto text-muted-foreground italic truncate">
                      iOS: “{h.mobilePreview}…”
                    </span>
                  )}
                </div>
              );
            })()}
            {coachSuggestions.length > 0 && (
              <div className="mt-2 space-y-1" data-testid="bulk-email-coach-suggestions">
                <div className="text-[10px] uppercase tracking-widest text-primary flex items-center gap-2">
                  <Wand2 className="w-3 h-3" /> AI suggestions · tap to use
                  <button type="button" onClick={() => setCoachSuggestions([])}
                          className="ml-auto normal-case tracking-normal underline text-muted-foreground">
                    Dismiss
                  </button>
                </div>
                <div className="space-y-1">
                  {coachSuggestions.map((s, i) => (
                    <button key={i} type="button"
                            onClick={() => {
                              if (showSubjectB && !subjectB && i > 0) {
                                setSubjectB(s);
                                toast.success("Applied as Subject B");
                              } else {
                                setSubject(s);
                                toast.success("Applied as Subject");
                              }
                            }}
                            className="w-full text-left text-xs border border-border p-1.5 hover:bg-primary/5 hover:border-primary/50"
                            data-testid={`bulk-email-coach-suggestion-${i}`}>
                      <span className="font-mono text-[10px] text-muted-foreground mr-2">{s.length}c</span>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          {showSubjectB && (
            <div>
              <Label className="text-xs flex items-center gap-2">
                Subject B
                <span className="text-[10px] text-muted-foreground font-normal">
                  · 50/50 split · winner surfaces on Campaign Analytics
                </span>
              </Label>
              <Input value={subjectB} onChange={(e) => setSubjectB(e.target.value)}
                     placeholder="Alternate subject to test open rate"
                     className="rounded-none" data-testid="bulk-email-subject-b" />
              {(() => {
                const h = computeSubjectHint(subjectB);
                return (
                  <div className={`text-[10px] mt-1 flex items-center gap-2 ${h.tone}`}
                       data-testid="bulk-email-subject-b-hint">
                    <span className="font-mono">{h.len} chars</span>
                    <span>· {h.msg}</span>
                    {h.len > 40 && (
                      <span className="ml-auto text-muted-foreground italic truncate">
                        iOS: “{h.mobilePreview}…”
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
          <div>
            <Label className="text-xs">Body — supports {`{{name}}, {{first_name}}, {{company}}, {{email}}, {{title}}, {{industry}}, {{owner_name}}`}</Label>
            <div className="rounded-none border border-input"
                 data-testid="bulk-email-body-editor">
              <SmartRichText value={bodyHtml} onChange={setBodyHtml}
                              placeholder="Hi {{first_name}}, quick note about…"
                              data-testid="bulk-email-body" />
            </div>
          </div>
          {signaturePreview && (
            <div data-testid="bulk-email-signature-preview">
              <Label className="text-xs flex items-center gap-2">
                Signature (auto-appended)
                <span className="text-[10px] text-muted-foreground font-normal">
                  · {mailboxId ? "picked from your mailbox → user → workspace default"
                              : "System Default · your signature preferred, workspace signature as fallback"}
                </span>
              </Label>
              <div className="border border-dashed border-border p-3 text-xs bg-muted/20 max-h-[140px] overflow-y-auto"
                   dangerouslySetInnerHTML={{ __html: signaturePreview }} />
            </div>
          )}
          {/* R135c · Attachments (collapsed by default — click to expand) */}
          <details className="border border-border" data-testid="bulk-email-attachments-section" open={openSections.attachments} onToggle={(e) => { if (e.target.open !== openSections.attachments) toggleSection("attachments"); }}>
            <summary className="cursor-pointer select-none px-3 py-2 flex items-center justify-between hover:bg-muted/40">
              <span className="text-xs font-semibold">Attachments{attachments.length ? ` · ${attachments.length}` : ""}</span>
              <span className="text-[10px] text-muted-foreground uppercase tracking-widest">PDF · image · ≤ 10 MB each</span>
            </summary>
            <div className="px-3 pb-3">
            <Label className="text-xs">Attachments (PDF, image, etc. — max 10 files, 10 MB each)</Label>
            <div className="flex items-center gap-2 mt-1">
              <label className="cursor-pointer">
                <input type="file"
                       onChange={(e) => onAttachmentPick(e.target.files?.[0])}
                       className="hidden"
                       data-testid="bulk-email-attach-input" />
                <span className={`inline-block px-3 py-1.5 border border-border text-xs rounded-none ${uploading ? "opacity-50" : "hover:bg-muted"}`}>
                  {uploading ? "Uploading…" : "+ Add attachment"}
                </span>
              </label>
              {attachments.length > 0 && (
                <span className="text-[10px] text-muted-foreground">
                  {attachments.length} file{attachments.length === 1 ? "" : "s"} attached · rides along with every email
                </span>
              )}
            </div>
            {attachments.length > 0 && (
              <div className="border border-border mt-2">
                {attachments.map((a) => (
                  <div key={a.file_id} className="flex items-center justify-between border-t border-border first:border-t-0 px-2 py-1.5 text-xs"
                       data-testid={`bulk-email-attach-${a.file_id}`}>
                    <span className="truncate">{a.filename}</span>
                    <span className="flex items-center gap-2 text-[10px] text-muted-foreground">
                      {Math.round((a.size || 0) / 1024)} KB
                      <button type="button" onClick={() => removeAttachment(a.file_id)}
                              className="underline hover:text-rose-600"
                              data-testid={`bulk-email-attach-remove-${a.file_id}`}>
                        remove
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            )}
            {/* R135d · Recent attachments quick-reuse */}
            {recentAttachments.length > 0 && (
              <div className="mt-2" data-testid="bulk-email-recent-attach">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                  Recent attachments — click to reuse
                </div>
                <div className="flex flex-wrap gap-1">
                  {recentAttachments
                    .filter((f) => !attachments.some((a) => a.file_id === f.id))
                    .slice(0, 8)
                    .map((f) => (
                      <button key={f.id} type="button"
                              onClick={() => setAttachments((prev) => [...prev, {
                                file_id: f.id,
                                filename: f.original_filename || "attachment",
                                size: f.size || 0,
                              }])}
                              className="px-2 py-1 border border-border text-[11px] rounded-none hover:bg-muted truncate max-w-[220px]"
                              title={`${f.original_filename} · ${Math.round((f.size||0)/1024)} KB`}
                              data-testid={`bulk-email-recent-attach-${f.id}`}>
                        + {(f.original_filename || "attachment").slice(0, 32)}
                      </button>
                    ))}
                </div>
              </div>
            )}
            </div>
          </details>
          {/* R135 · Send Time Optimizer */}
          <details className="border border-border" open={openSections.schedule} onToggle={(e) => { if (e.target.open !== openSections.schedule) toggleSection("schedule"); }}>
            <summary className="cursor-pointer select-none px-3 py-2 flex items-center justify-between hover:bg-muted/40">
              <span className="text-xs font-semibold">Schedule & send window{scheduledAt || sendWindow ? " · configured" : ""}</span>
              <span className="text-[10px] text-muted-foreground uppercase tracking-widest">Later · Time-of-day · TZ</span>
            </summary>
            <div className="px-3 pb-3 pt-2 grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Schedule for later (optional)</Label>
              <Input type="datetime-local" value={scheduledAt}
                     onChange={(e) => setScheduledAt(e.target.value)}
                     className="rounded-none" data-testid="bulk-email-schedule" />
              <div className="text-[10px] text-muted-foreground mt-1">
                Blank = fire now. Set a future time to queue until then.
              </div>
            </div>
            <div>
              <Label className="text-xs">Send window (recipient local)</Label>
              <Input value={sendWindow} onChange={(e) => setSendWindow(e.target.value)}
                     placeholder="09:00-18:00" className="rounded-none"
                     data-testid="bulk-email-window" />
              <div className="text-[10px] text-muted-foreground mt-1">
                Hold sends until this local window opens. Empty = fire immediately.
              </div>
            </div>
            {sendWindow && (
              <div>
                <Label className="text-xs">Fallback timezone</Label>
                <Input value={defaultTz} onChange={(e) => setDefaultTz(e.target.value)}
                       placeholder="Asia/Kolkata"
                       className="rounded-none" data-testid="bulk-email-default-tz" />
                <div className="text-[10px] text-muted-foreground mt-1">
                  Used when lead has no timezone or country.
                </div>
              </div>
            )}
            </div>
          </details>

          {/* R135c · Recipient preview (cycling 3 at a time up to 12) */}
          {showPreview && preview && (
            <div className="border border-primary/40 bg-primary/5 p-2 space-y-2"
                 data-testid="bulk-email-preview">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-primary">
                <span>Recipient preview — {previewIdx + 1}-{Math.min(previewIdx + 3, preview.length)} of {preview.length}</span>
                <div className="flex items-center gap-2 normal-case tracking-normal">
                  {preview.length > 3 && (
                    <>
                      <button type="button"
                              onClick={() => setPreviewIdx((v) => Math.max(0, v - 3))}
                              disabled={previewIdx === 0}
                              className="underline disabled:opacity-40"
                              data-testid="bulk-email-preview-prev">
                        ← Prev
                      </button>
                      <button type="button"
                              onClick={() => setPreviewIdx((v) => Math.min(preview.length - 3, v + 3))}
                              disabled={previewIdx + 3 >= preview.length}
                              className="underline disabled:opacity-40"
                              data-testid="bulk-email-preview-next">
                        Next →
                      </button>
                    </>
                  )}
                  <button type="button" onClick={() => setShowPreview(false)}
                          className="underline">Close</button>
                </div>
              </div>
              {preview.slice(previewIdx, previewIdx + 3).map((lead, i) => {
                // R135d · If subject_b is set, alternate which subject
                // this specific lead would receive (matches the server's
                // even/odd index split).
                const absoluteIdx = previewIdx + i;
                const isB = showSubjectB && subjectB.trim() && absoluteIdx % 2 === 1;
                const rendered = renderMergeTags(isB ? subjectB : subject, lead);
                const renderedBody = renderMergeTags(bodyHtml, lead);
                const fullHtml = renderedBody
                  + (signaturePreview ? `<div style="margin-top:32px;padding-top:12px;border-top:1px solid #e5e7eb;font-size:13px;color:#4b5563">${signaturePreview}</div>` : "");
                return (
                  <div key={i} className="border border-primary/20 bg-white p-2 text-xs"
                       data-testid={`bulk-email-preview-row-${i}`}>
                    <div className="text-[10px] text-muted-foreground flex items-center gap-2">
                      To: {lead.email || "—"} · {lead.name || "—"}
                      {showSubjectB && subjectB.trim() && (
                        <span className={`text-[9px] px-1 border ${isB ? "border-fuchsia-300 text-fuchsia-700" : "border-sky-300 text-sky-700"}`}>
                          variant {isB ? "B" : "A"}
                        </span>
                      )}
                    </div>
                    <div className="font-semibold mt-1">Subject: {rendered || <span className="italic text-muted-foreground">(empty)</span>}</div>
                    <div className="mt-2 max-h-[200px] overflow-y-auto border border-dashed border-primary/20 p-2 bg-muted/10"
                         dangerouslySetInnerHTML={{ __html: fullHtml }} />
                  </div>
                );
              })}
            </div>
          )}

          {/* R138ai · Suppression Auto-Skip Alert — sample emails + reasons */}
          {!campaign && suppressionCount > 0 && (
            <div className="border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900"
                 data-testid="bulk-email-suppression-notice">
              <div className="flex items-center gap-2 font-semibold">
                <AlertTriangle className="w-3 h-3" />
                <span>
                  {suppressionCount} of {suppressionTotal || suppressionCount} recipient{suppressionCount === 1 ? "" : "s"} on the suppression list
                </span>
                <span className="ml-auto text-[10px] uppercase tracking-widest">will be silently skipped</span>
              </div>
              {Object.keys(suppressionByReason).length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1"
                     data-testid="bulk-email-suppression-reasons">
                  {Object.entries(suppressionByReason).map(([reason, n]) => (
                    <span key={reason} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-amber-400 bg-white/60">
                      {reason}: {n}
                    </span>
                  ))}
                </div>
              )}
              {suppressionSample.length > 0 && (
                <details className="mt-1">
                  <summary className="text-[11px] cursor-pointer underline decoration-dotted">
                    Show {Math.min(suppressionSample.length, 20)} sample email{suppressionSample.length === 1 ? "" : "s"}
                  </summary>
                  <ul className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 text-[10.5px] font-mono">
                    {suppressionSample.map((s) => (
                      <li key={s.email} className="truncate" title={`${s.email} · ${s.reason || "unknown"}${s.detail ? " · " + s.detail : ""}`}>
                        {s.email} <span className="text-amber-700">· {s.reason || "unknown"}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              <div className="mt-0.5 text-[10.5px]">
                Suppressed addresses (unsubscribed, hard-bounced, or manually blocked) are skipped to protect sender reputation. Manage them in <span className="underline">Campaign Analytics → Suppressions</span>.
              </div>
            </div>
          )}
          {/* R135e · Rate-limit + deliverability advisory */}
          {!campaign && rateWarning && (
            <div className={`border p-2 text-xs ${
              rateWarning.level === "danger" ? "border-rose-300 bg-rose-50 text-rose-800" :
              rateWarning.level === "warn" ? "border-amber-300 bg-amber-50 text-amber-800" :
              "border-emerald-300 bg-emerald-50 text-emerald-800"
            }`} data-testid="bulk-email-rate-warning">
              <div className="font-semibold flex items-center gap-2">
                <span>{rateWarning.level === "ok" ? "Rate check" : "Rate limit warning"}</span>
                <span className="ml-auto text-[10px] uppercase tracking-widest">{rateWarning.level}</span>
              </div>
              <div className="mt-0.5">{rateWarning.msg}</div>
            </div>
          )}
          {!campaign && (subject.trim() || bodyHtml.trim()) && (
            <div className={`border p-2 text-xs ${
              deliverability.score >= 80 ? "border-emerald-300 bg-emerald-50" :
              deliverability.score >= 60 ? "border-amber-300 bg-amber-50" :
              "border-rose-300 bg-rose-50"
            }`} data-testid="bulk-email-deliverability">
              <div className="flex items-center gap-2 font-semibold">
                <span>Deliverability score</span>
                <span className={`ml-auto font-mono ${
                  deliverability.score >= 80 ? "text-emerald-700" :
                  deliverability.score >= 60 ? "text-amber-700" :
                  "text-rose-700"
                }`} data-testid="bulk-email-deliverability-score">
                  {deliverability.score}/100
                </span>
              </div>
              {deliverability.flags.length > 0 && (
                <ul className="mt-1 space-y-0.5 list-disc pl-4 text-[11px]">
                  {deliverability.flags.map((f, i) => (
                    <li key={i} className={
                      f.level === "danger" ? "text-rose-700" :
                      f.level === "warn" ? "text-amber-700" :
                      "text-neutral-600"
                    }>{f.msg}</li>
                  ))}
                </ul>
              )}
              {deliverability.flags.length === 0 && (
                <div className="mt-0.5 text-[11px] text-emerald-700">All good — no obvious red flags.</div>
              )}
            </div>
          )}
          {campaign && (
            <div className="border border-amber-300 bg-amber-50 p-2 space-y-1"
                 data-testid="bulk-email-campaign-progress">
              <div className="flex items-center gap-2 text-xs">
                <Rocket className="w-3 h-3 text-amber-700" />
                <span className="font-semibold">Campaign · {campaign.state}</span>
                <span className="ml-auto font-mono tabular-nums text-amber-800">
                  {campaign.processed || 0} / {campaign.total || 0} · sent {campaign.sent || 0}
                  {campaign.failed ? ` · failed ${campaign.failed}` : ""}
                  {campaign.skipped ? ` · skipped ${campaign.skipped}` : ""}
                </span>
              </div>
              <div className="h-2 bg-amber-200">
                <div className={`h-2 transition-all ${campaign.state === "done" ? "bg-emerald-500" : "bg-amber-500"}`}
                     style={{ width: `${pct}%` }}
                     data-testid="bulk-email-progress-bar" />
              </div>
              {campaign.state === "done" && (campaign.failed || 0) > 0 && (
                <Button size="sm" variant="outline"
                        className="rounded-none h-7 mt-1 border-rose-400 text-rose-700 hover:bg-rose-50"
                        data-testid="bulk-email-retry-failed"
                        onClick={async () => {
                          if (!window.confirm(`Retry ${campaign.failed} failed recipient${campaign.failed === 1 ? "" : "s"} in a fresh campaign?`)) return;
                          try {
                            const rr = await api.post(
                              `/workspaces/${wsId}/sales/campaigns/${campaign.campaign_id}/retry-failed`);
                            toast.success(`Retry queued · ${rr.data?.total || 0} recipient${rr.data?.total === 1 ? "" : "s"}`);
                            // Follow the new campaign so we watch the retry live.
                            if (rr.data?.campaign_id) {
                              setCampaign({ campaign_id: rr.data.campaign_id,
                                             total: rr.data.total,
                                             state: "starting",
                                             sent: 0, failed: 0, skipped: 0, processed: 0 });
                            }
                          } catch (e) { toast.error(apiErrorMessage(e, "Retry failed")); }
                        }}>
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Retry {campaign.failed} failed
                </Button>
              )}
            </div>
          )}
        </div>
        </div>
        <DialogFooter className="flex-wrap gap-2 px-5 py-3 border-t border-border flex-shrink-0">
          <Button variant="outline" onClick={onClose} className="rounded-none"
                  data-testid="bulk-email-cancel">Close</Button>
          <Button variant="outline" onClick={saveAsTemplate}
                  disabled={savingTpl || !subject.trim() || !bodyHtml.trim()}
                  className="rounded-none" data-testid="bulk-email-save-template">
            {savingTpl ? "Saving…" : "Save as template"}
          </Button>
          <Button variant="outline" onClick={loadPreview}
                  disabled={busy} className="rounded-none"
                  data-testid="bulk-email-preview-btn">
            Preview
          </Button>
          <Button onClick={start} disabled={busy || (campaign && campaign.state !== "done")}
                  className="rounded-none" data-testid="bulk-email-start">
            <Send className="w-3 h-3 mr-1" /> {campaign ? "Restart with new content" : "Start campaign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

